import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import {
  ProfileSettingsModal,
  FieldTextInput,
  FieldSelectModern,
  FieldTextEditor,
  ToggleSwitch,
  Form,
} from '../../components';
import { generateMonthsList, generateYearsList } from '../../util/dates';
import { SlOptionsVertical } from 'react-icons/sl';
import { MdOutlineRateReview } from 'react-icons/md';
import { AiOutlineClose, AiOutlineDelete } from 'react-icons/ai';
import { IoMdCheckmark } from 'react-icons/io';

import css from '../../styles/profileSettingsEntryForm.css';

const START_YEAR = 1900;

const REQUIRED_FIELDS = [
  'customerName',
  'task',
  'description',
  'startMonth',
  'startYear',
  'taskType',
];

const validateForm = (values, intl) => {
  const errors = {};
  REQUIRED_FIELDS.forEach(field => {
    if (field === 'taskType') {
      if (!values.taskType || (Array.isArray(values.taskType) && values.taskType.length === 0)) {
        errors.taskType = intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.required' });
      }
    } else if (!values[field]) {
      errors[field] = intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.required' });
    }
  });

  if (!values.jobContinues) {
    if (!values.endMonth) {
      errors.endMonth = intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.required' });
    }
    if (!values.endYear) {
      errors.endYear = intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.required' });
    }

    if (values.startYear && values.endYear && values.startMonth && values.endMonth) {
      const startDate = new Date(
        values.startYear?.key || values.startYear,
        (values.startMonth?.key || values.startMonth) - 1
      );
      const endDate = new Date(
        values.endYear?.key || values.endYear,
        (values.endMonth?.key || values.endMonth) - 1
      );
      if (endDate < startDate) {
        errors.endMonth = intl.formatMessage({
          id: 'ProfileSettingsWorkExperienceForm.endBeforeStart',
        });
      }
    }
  }

  return errors;
};

const buildInitialValues = editingItem => {
  if (!editingItem) {
    return {
      customerName: '',
      task: '',
      description: '',
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      jobContinues: false,
      taskType: [],
    };
  }

  return {
    customerName: editingItem.customerName || '',
    task: editingItem.task || '',
    description: editingItem.description || '',
    startMonth: editingItem.startMonth
      ? {
          key: editingItem.startMonth,
          label: editingItem.startMonth,
          value: editingItem.startMonth,
        }
      : null,
    startYear: editingItem.startYear
      ? { key: editingItem.startYear, label: editingItem.startYear, value: editingItem.startYear }
      : null,
    endMonth: editingItem.endMonth
      ? { key: editingItem.endMonth, label: editingItem.endMonth, value: editingItem.endMonth }
      : null,
    endYear: editingItem.endYear
      ? { key: editingItem.endYear, label: editingItem.endYear, value: editingItem.endYear }
      : null,
    jobContinues: editingItem.jobContinues || false,
    taskType: editingItem.taskType
      ? Array.isArray(editingItem.taskType)
        ? editingItem.taskType
        : [editingItem.taskType]
      : [],
  };
};

const DateRangeFields = ({ form, values, monthsList, yearsList, fieldsLocked, labels }) => {
  const startYearKey = values.startYear?.key ?? null;
  const startMonthKey = values.startMonth?.key ?? null;
  const endYearKey = values.endYear?.key ?? null;
  const endMonthKey = values.endMonth?.key ?? null;

  const filteredEndYears = useMemo(
    () => (startYearKey ? yearsList.filter(y => y.key >= startYearKey) : yearsList),
    [yearsList, startYearKey]
  );

  const filteredEndMonths = useMemo(() => {
    if (startYearKey && endYearKey && startYearKey === endYearKey && startMonthKey) {
      return monthsList.filter(m => m.key >= startMonthKey);
    }
    return monthsList;
  }, [monthsList, startYearKey, endYearKey, startMonthKey]);

  useEffect(() => {
    if (!startYearKey || !endYearKey) return;
    if (endYearKey < startYearKey) {
      form.change('endYear', null);
      form.change('endMonth', null);
      return;
    }
    if (
      endYearKey === startYearKey &&
      startMonthKey &&
      endMonthKey &&
      endMonthKey < startMonthKey
    ) {
      form.change('endMonth', null);
    }
  }, [startYearKey, startMonthKey, endYearKey, endMonthKey, form]);

  return (
    <div className={css.dateRow}>
      <div className={css.dateField}>
        <FieldSelectModern
          id="startMonth"
          name="startMonth"
          options={monthsList}
          placeholder={labels.monthPlaceholder}
          label={labels.startMonth}
          disabled={fieldsLocked}
        />
      </div>
      <div className={css.dateField}>
        <FieldSelectModern
          id="startYear"
          name="startYear"
          options={yearsList}
          placeholder={labels.yearPlaceholder}
          label={labels.startYear}
          disabled={fieldsLocked}
        />
      </div>
      <div className={css.dateField}>
        <FieldSelectModern
          id="endMonth"
          name="endMonth"
          options={filteredEndMonths}
          placeholder={labels.monthPlaceholder}
          label={labels.endMonth}
          disabled={fieldsLocked || values.jobContinues}
        />
      </div>
      <div className={css.dateField}>
        <FieldSelectModern
          id="endYear"
          name="endYear"
          options={filteredEndYears}
          placeholder={labels.yearPlaceholder}
          label={labels.endYear}
          disabled={fieldsLocked || values.jobContinues}
        />
      </div>
    </div>
  );
};

const TypeCheckbox = ({ label, checked, disabled, onClick }) => {
  const checkboxClass = disabled
    ? css.checkboxDisabled
    : checked
    ? css.checkboxChecked
    : css.checkbox;

  return (
    <button
      type="button"
      className={disabled ? css.typeOptionDisabled : css.typeOption}
      onClick={disabled ? undefined : onClick}
    >
      <span className={checkboxClass}>
        {checked ? <IoMdCheckmark className={css.checkboxIcon} /> : null}
      </span>
      <span className={disabled ? css.typeLabelDisabled : css.typeLabel}>{label}</span>
    </button>
  );
};

const ProfileSettingsWorkExperienceForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    editingItem,
    initialIsPublic,
    onSubmit,
    onDelete,
    testimonialState,
    onOpenRequestTestimonial,
    updateInProgress,
    intl,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirmPending, setDeleteConfirmPending] = useState(false);
  const [isPublic, setIsPublic] = useState(initialIsPublic ?? true);
  const deleteTimeoutRef = useRef(null);

  const monthsList = useMemo(() => generateMonthsList(), []);
  const yearsList = useMemo(() => generateYearsList(START_YEAR), []);
  const initialValues = useMemo(() => buildInitialValues(editingItem), [editingItem]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMenuOpen(false);
      setDeleteConfirmPending(false);
      setIsPublic(initialIsPublic ?? true);
    }
    return () => {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
    };
  }, [isOpen, initialIsPublic]);

  const isNew = !editingItem;
  const fieldsLocked = !!testimonialState;
  const descriptionLocked = fieldsLocked;
  const fullyLocked = fieldsLocked;

  const handleDeleteClick = useCallback(() => {
    if (deleteConfirmPending) {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
      onDelete(editingItem.id);
      onClose();
    } else {
      setDeleteConfirmPending(true);
      deleteTimeoutRef.current = setTimeout(() => {
        setDeleteConfirmPending(false);
      }, 3000);
    }
  }, [deleteConfirmPending, editingItem, onDelete, onClose]);

  const handleTypeChange = useCallback((value, form) => {
    const current = form.getState().values.taskType || [];
    if (value === 'noType') {
      form.change('taskType', ['noType']);
    } else {
      const withoutNoType = current.filter(v => v !== 'noType');
      if (withoutNoType.includes(value)) {
        form.change(
          'taskType',
          withoutNoType.filter(v => v !== value)
        );
      } else {
        form.change('taskType', [...withoutNoType, value]);
      }
    }
  }, []);

  // Labels
  const labels = {
    title: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.title' }),
    subtitle: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.subtitle' }),
    save: intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' }),
    company: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.companyLabel' }),
    companyPlaceholder: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.companyPlaceholder',
    }),
    position: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.positionLabel' }),
    positionPlaceholder: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.positionPlaceholder',
    }),
    description: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.descriptionLabel' }),
    descriptionPlaceholder: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.descriptionPlaceholder',
    }),
    startMonth: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.startMonthLabel' }),
    startYear: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.startYearLabel' }),
    endMonth: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.endMonthLabel' }),
    endYear: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.endYearLabel' }),
    monthPlaceholder: intl.formatMessage({ id: 'SignupProviderForms.monthPlaceholder' }),
    yearPlaceholder: intl.formatMessage({ id: 'SignupProviderForms.yearPlaceholder' }),
    stillOngoing: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.stillOngoing' }),
    typeLabel: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.typeLabel' }),
    typeB2B: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.typeB2B' }),
    typeB2C: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.typeB2C' }),
    typeNotDefined: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.typeNotDefined' }),
    publicLabel: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.publicLabel' }),
    publicExplanation: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.publicExplanation',
    }),
    privateExplanation: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.privateExplanation',
    }),
    requestTestimonial: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.requestTestimonial',
    }),
    cancel: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.cancel' }),
    close: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.close' }),
    discardChanges: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.discardChanges' }),
    deleteLabel: intl.formatMessage({ id: 'ProfileSettingsWorkExperienceForm.delete' }),
    confirmDeletion: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.confirmDeletion',
    }),
    testimonialWarning: intl.formatMessage({
      id: 'ProfileSettingsWorkExperienceForm.testimonialWarning',
    }),
  };

  return (
    <FinalForm
      key={isOpen ? 'open' : 'closed'}
      initialValues={initialValues}
      validate={values => validateForm(values, intl)}
      onSubmit={values => {
        onSubmit({ item: values, isPublic });
        onClose();
      }}
      render={({ handleSubmit, dirty, invalid, values, form }) => {
        const taskTypeChanged =
          JSON.stringify(values.taskType || []) !== JSON.stringify(initialValues.taskType || []);
        const publicChanged = isPublic !== (initialIsPublic ?? true);
        const isFormDirty = dirty || taskTypeChanged || publicChanged;
        const canRequestTestimonial = !isNew && !isFormDirty && !testimonialState;
        const submitDisabled = fullyLocked || !isFormDirty || invalid;

        // Build menu items
        const buildMenuItems = () => {
          const items = [];

          // 1. Request testimonial (always shown)
          items.push({
            key: 'requestTestimonial',
            label: labels.requestTestimonial,
            icon: <MdOutlineRateReview className={css.menuItemIcon} />,
            onClick: canRequestTestimonial
              ? () => {
                  setMenuOpen(false);
                  onOpenRequestTestimonial(editingItem);
                }
              : undefined,
            disabled: !canRequestTestimonial,
            variant: canRequestTestimonial ? 'primary' : 'disabled',
          });

          // 2. Cancel / Close / Discard changes
          if (isNew) {
            items.push({
              key: 'cancel',
              label: labels.cancel,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                setMenuOpen(false);
                onClose();
              },
              variant: 'default',
            });
          } else if (isFormDirty) {
            items.push({
              key: 'discard',
              label: labels.discardChanges,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                form.reset();
                setIsPublic(initialIsPublic ?? true);
                setMenuOpen(false);
              },
              variant: 'default',
            });
          } else {
            items.push({
              key: 'close',
              label: labels.close,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                setMenuOpen(false);
                onClose();
              },
              variant: 'default',
            });
          }

          // 3. Delete (only existing items)
          if (!isNew) {
            items.push({
              key: 'delete',
              label: deleteConfirmPending ? labels.confirmDeletion : labels.deleteLabel,
              icon: <AiOutlineDelete className={css.menuItemIcon} />,
              onClick: handleDeleteClick,
              variant: 'danger',
              confirm: deleteConfirmPending,
            });
          }

          return items;
        };

        const menuItems = buildMenuItems();

        const kebabMenu = (
          <div className={css.kebabWrapper}>
            <button
              type="button"
              className={css.kebabTrigger}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <SlOptionsVertical className={css.kebabIcon} />
            </button>
            {menuOpen ? (
              <>
                <div
                  className={css.menuBackdrop}
                  onClick={() => {
                    setMenuOpen(false);
                    setDeleteConfirmPending(false);
                  }}
                />
                <div className={css.kebabDropdown}>
                  {menuItems.map(item => {
                    const className = item.disabled
                      ? css.menuItemDisabled
                      : item.confirm
                      ? css.menuItemConfirm
                      : item.variant === 'primary'
                      ? css.menuItemPrimary
                      : item.variant === 'danger'
                      ? css.menuItemDanger
                      : css.menuItem;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={className}
                        onClick={item.disabled ? undefined : item.onClick}
                        disabled={item.disabled}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        );

        // Footer override for testimonial items
        const footerContent = fieldsLocked ? (
          <div className={css.footerWithWarning}>
            <span className={css.testimonialWarning}>{labels.testimonialWarning}</span>
          </div>
        ) : null;

        return (
          <ProfileSettingsModal
            id="workExperience"
            containerClassName={css.wideContainer}
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            title={labels.title}
            subtitle={labels.subtitle}
            headerAction={kebabMenu}
            onSubmit={handleSubmit}
            submitDisabled={submitDisabled}
            submitInProgress={updateInProgress}
            submitLabel={labels.save}
          >
            {footerContent}
            <Form className={css.formContent} onSubmit={() => {}}>
              {/* Company name */}
              <FieldTextInput
                id="customerName"
                name="customerName"
                type="text"
                label={labels.company}
                placeholder={labels.companyPlaceholder}
                disabled={fieldsLocked}
              />

              {/* Position */}
              <FieldTextInput
                id="task"
                name="task"
                type="text"
                label={labels.position}
                placeholder={labels.positionPlaceholder}
                disabled={fieldsLocked}
              />

              {/* Description */}
              <FieldTextEditor
                id="description"
                name="description"
                label={labels.description}
                placeholder={labels.descriptionPlaceholder}
                isExperienceDescription
                disabled={descriptionLocked}
              />

              {/* Date row */}
              <DateRangeFields
                form={form}
                values={values}
                monthsList={monthsList}
                yearsList={yearsList}
                fieldsLocked={fieldsLocked}
                labels={labels}
              />

              {/* Still ongoing toggle */}
              <ToggleSwitch
                isOn={values.jobContinues}
                onToggle={val => form.change('jobContinues', val)}
                defaultState={false}
                label={labels.stillOngoing}
                disabled={fieldsLocked}
              />

              {/* Type checkboxes */}
              <div className={css.typeContainer}>
                <span className={css.label}>{labels.typeLabel}</span>
                <div className={css.typeCheckboxes}>
                  <TypeCheckbox
                    label={labels.typeB2B}
                    checked={(values.taskType || []).includes('B2B')}
                    disabled={fieldsLocked}
                    onClick={() => handleTypeChange('B2B', form)}
                  />
                  <TypeCheckbox
                    label={labels.typeB2C}
                    checked={(values.taskType || []).includes('B2C')}
                    disabled={fieldsLocked}
                    onClick={() => handleTypeChange('B2C', form)}
                  />
                  <TypeCheckbox
                    label={labels.typeNotDefined}
                    checked={(values.taskType || []).includes('noType')}
                    disabled={fieldsLocked}
                    onClick={() => handleTypeChange('noType', form)}
                  />
                </div>
              </div>

              {/* Public toggle */}
              <div>
                <ToggleSwitch
                  isOn={isPublic}
                  onToggle={setIsPublic}
                  defaultState={true}
                  label={labels.publicLabel}
                  disabled={fieldsLocked}
                />
                <div className={css.publicExplanation}>
                  {isPublic ? labels.publicExplanation : labels.privateExplanation}
                </div>
              </div>
            </Form>
          </ProfileSettingsModal>
        );
      }}
    />
  );
};

ProfileSettingsWorkExperienceForm.defaultProps = {
  editingItem: null,
  initialIsPublic: true,
  testimonialState: null,
  updateInProgress: false,
};

ProfileSettingsWorkExperienceForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  editingItem: PropTypes.object,
  initialIsPublic: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  testimonialState: PropTypes.oneOf(['pending', 'reviewed', 'published', null]),
  onOpenRequestTestimonial: PropTypes.func.isRequired,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsWorkExperienceForm);
